/**
 * Restore the `text=` attribute ExcelJS drops from `containsText`-family
 * conditional-formatting rules, so the merged SDMS workbook still opens in the
 * legacy ALPACA (DevExpress ASPxSpreadsheet) viewer.
 *
 * WHY (root-caused 2026-09-15, batch QB26091501_001 "won't open in ALPACA").
 * The SDMS templates carry QC-colouring rules like
 *
 *     <cfRule priority="9" dxfId="3" type="containsText" text="=&quot;False&quot;">
 *       <formula>NOT(ISERROR(SEARCH("False",AH9)))</formula>
 *     </cfRule>
 *
 * `mergeSdmsTemplate` (and the dept-chain injector) round-trip the template
 * through ExcelJS — `wb.xlsx.load()` … `wb.xlsx.writeBuffer()` — to bake in the
 * merged rows. ExcelJS re-emits those rules WITHOUT the `text` attribute:
 *
 *     <cfRule type="containsText" dxfId="3" priority="9">
 *       <formula>NOT(ISERROR(SEARCH("False",AH9)))</formula>
 *     </cfRule>
 *
 * OOXML requires `text` on the containsText family. Univer does not, so the file
 * renders perfectly in Alpaca Intelligence — which is why this went unseen. But
 * DevExpress v20.1 throws `ArgumentNullException` deep in
 * `TextFormulaConditionalFormatting.AppendFindExpression` while APPLYING the rule,
 * so `Workbook.LoadDocument` returns FALSE and materialises only the first
 * worksheet. ALPACA's open path then indexes `Worksheets[1]` in
 * `SpreadsheetEntryController.CalibAndABIDSupport` and throws
 * "Worksheet index should be positive and less than the number of worksheets" —
 * the batch will not open, and the analyst sees a blank sheet.
 *
 * Proven against the real DevExpress v20.1 engine on QB26091501_001:
 *     as merged (text= dropped)   LoadDocument=False, 1 sheet
 *     text= restored              LoadDocument=True,  3 sheets, all cells present
 *
 * The dropped value is still present in each rule's own `SEARCH("…")` formula, so
 * it is recovered from there and re-emitted in the template's exact form
 * (`text="=&quot;False&quot;"`). Nothing outside those `<cfRule>` tags is touched.
 * This mirrors the one-off repair `deploy/db/one-off/Repair-SdmsWorkbookCfText-2026-08-21.ps1`
 * that fixed the workbooks ALREADY stored; this module stops NEW ones being damaged
 * at the source (the merge), which that script's "surgical patcher" claim missed —
 * the surgical patcher only spares EDIT saves; the merge still re-serialises.
 */
import JSZip from 'jszip';

/** cfRule types that OOXML requires carry a `text` attribute. */
const TEXT_RULE_TYPE = /\btype="(?:containsText|notContainsText|beginsWith|endsWith)"/;

/**
 * Pure transform over ONE worksheet XML string, restoring the two cfRule attributes
 * ExcelJS drops on write:
 *   (1) containsText-family rules lose their `text` attribute — recovered from the
 *       rule's own `SEARCH("term", …)` formula and re-added as `text="=&quot;term&quot;"`
 *       (or `SEARCH($P$7, …)` → `text="=$P$7"` when the term is a cell).
 *   (2) `containsBlanks` rules lose their `stopIfTrue` — OG carries it on EVERY
 *       containsBlanks rule so a blank cell stops before the numeric range rules
 *       (cellIs lessThan/greaterThan) paint it. Without it, DevExpress evaluates
 *       those rules on an empty Rec%/RPD cell (VALUE("") → 0, out of range) and
 *       colours it red — the batch opens correctly in Alpaca Intelligence (Univer
 *       detects the guard by operator) but reads wrong in ALPACA (client 2026-09-15).
 * Rules already intact, or whose term can't be recovered, are left exactly as they
 * are. Exported for unit testing.
 */
export function restoreCfTextInSheetXml(xml: string): string {
  return xml.replace(/<cfRule\b([^>]*)>([\s\S]*?)<\/cfRule>/g, (whole, attrs: string, body: string) => {
    let a = attrs;
    // (1) containsText family — re-add `text=` from the rule's SEARCH("..") formula. The term may
    //     also be a CELL (PFAS_533_Waters 8114: `SEARCH($P$7,AF2)`, template `text="=$P$7"`) — that
    //     rule was left bare and DevExpress still refused the workbook (2026-09-26).
    if (TEXT_RULE_TYPE.test(a) && !/\btext=/.test(a)) {
      const m = /SEARCH\(\s*(?:&quot;|")([^"&]*)(?:&quot;|")/.exec(body);
      const ref = m ? null : /SEARCH\(\s*(\$?[A-Z]{1,3}\$?\d+)\s*,/.exec(body);
      if (m) a = `${a} text="=&quot;${m[1]}&quot;"`;
      else if (ref) a = `${a} text="=${ref[1]}"`;
    }
    // (2) containsBlanks blank-guard — re-add `stopIfTrue="1"`.
    if (/\btype="containsBlanks"/.test(a) && !/\bstopIfTrue=/.test(a))
      a = `${a} stopIfTrue="1"`;
    return a === attrs ? whole : `<cfRule${a}>${body}</cfRule>`;
  });
}

/**
 * Restore the `text=` attribute across every worksheet in an ExcelJS-written
 * `.xlsx` buffer. Returns the input UNCHANGED (same reference) when no rule needed
 * repair, so it is a cheap no-op on templates that carry no such rules.
 */
export async function repairCfTextAttrs(buf: ArrayBuffer): Promise<ArrayBuffer> {
  const zip = await JSZip.loadAsync(buf);
  let changed = false;
  for (const path of Object.keys(zip.files)) {
    if (!/^xl\/worksheets\/sheet\d+\.xml$/.test(path)) continue;
    const xml = await zip.file(path)!.async('string');
    const out = restoreCfTextInSheetXml(xml);
    if (out !== xml) { zip.file(path, out, { createFolders: false }); changed = true; }
  }
  if (!changed) return buf;
  return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' }) as Promise<ArrayBuffer>;
}

// ── Band conditional-formatting expansion ───────────────────────────────────
//
// WHY (client 2026-09-15, "colour only on the first detail row O9"). The SDMS
// templates author each detail-band conditional-formatting block on the FIRST
// band row only (`<conditionalFormatting sqref="O9">`, `T9`, `AH9`, …). Legacy
// ALPACA's DevExpress merge REPLICATES each such block down the whole data band —
// one block per row, with the sqref AND every relative reference in its formulas
// row-shifted (O9→O10→…→O21, VALUE(Z9)→VALUE(Z10), SEARCH("Fail",T9)→…T10). AI's
// client merge repeats the band CELLS but not the CF blocks, so ALPACA paints only
// row 9. Alpaca Intelligence's OWN view is unaffected — it band-expands the
// template's row-9 CF into Univer at render time (sdmsConditionalFormat.ts) — so
// this touches ONLY the persisted workbook that ALPACA reads.

/** Row of an A1 cell ("O9" → 9, "AL9" → 9). */
function rowOfA1(ref: string): number {
  const m = /(\d+)\s*$/.exec(ref.trim());
  return m ? parseInt(m[1]!, 10) : -1;
}

/** Shift the row number of every RELATIVE A1 reference in `s` by `offset`.
 *  A `$` before the digits (absolute row) is preserved unshifted; a whole-column
 *  ref like `G:G` has no digits and is untouched. */
function shiftRefsRow(s: string, offset: number): string {
  // A name followed by "(" is a function (LOG10), not a cell — see sdmsMailMerge.shiftFormula.
  return s.replace(/(\$?[A-Z]{1,3})(\$?)(\d+)(?!\d)(?!\s*\()/g, (_m, colPart: string, rowDollar: string, row: string) =>
    rowDollar ? `${colPart}${rowDollar}${row}` : `${colPart}${parseInt(row, 10) + offset}`);
}

/** Shift only the row parts of an sqref ("AL9:AM9" → "AL10:AM10"). */
function shiftSqrefRow(sqref: string, offset: number): string {
  return sqref.replace(/([A-Z]{1,3})(\d+)/g, (_m, col: string, row: string) => `${col}${parseInt(row, 10) + offset}`);
}

/**
 * Replicate each detail-band `<conditionalFormatting>` block down the merged data
 * band, matching legacy ALPACA's DevExpress merge, so the QC colouring shows on
 * every row when the workbook is opened in ALPACA. Operates ONLY on the persisted
 * blob (never the buffer Alpaca Intelligence renders). Returns the input unchanged
 * when there is nothing to expand.
 */
export async function expandBandConditionalFormatting(buf: ArrayBuffer): Promise<ArrayBuffer> {
  const zip = await JSZip.loadAsync(buf);
  let changed = false;
  for (const path of Object.keys(zip.files)) {
    if (!/^xl\/worksheets\/sheet\d+\.xml$/.test(path)) continue;
    const xml = await zip.file(path)!.async('string');
    const out = expandBandCfInSheetXml(xml);
    if (out !== xml) { zip.file(path, out, { createFolders: false }); changed = true; }
  }
  if (!changed) return buf;
  return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' }) as Promise<ArrayBuffer>;
}

/** One worksheet's XML with its detail-band conditional formatting replicated down the band (see
 *  expandBandConditionalFormatting) — the same string back when there is nothing to expand. Pure, so the Save can
 *  run it inside the cell patcher's single unzip / zip (speed, 2026-10-09). */
export function expandBandCfInSheetXml(xml: string): string {
  const BLOCK = /<conditionalFormatting\b[^>]*>[\s\S]*?<\/conditionalFormatting>/g;
  {
    // 1. Which row carries the detail band? = the row anchoring the MOST CF blocks.
    const blocks = xml.match(BLOCK) ?? [];
    if (blocks.length === 0) return xml;
    const byRow = new Map<number, number>();
    let maxPri = 0;
    for (const b of blocks) {
      const sq = /sqref="([^"]*)"/.exec(b)?.[1] ?? '';
      const anchor = Math.min(...sq.split(/\s+/).filter(Boolean).map(a => rowOfA1(a.split(':')[0]!)));
      if (anchor > 0) byRow.set(anchor, (byRow.get(anchor) ?? 0) + 1);
      for (const p of b.matchAll(/priority="(\d+)"/g)) maxPri = Math.max(maxPri, parseInt(p[1]!, 10));
    }
    let bandTop = -1, bandTopCount = 0;
    for (const [row, count] of byRow) if (count > bandTopCount) { bandTop = row; bandTopCount = count; }
    if (bandTop < 0 || bandTopCount < 2) return xml;   // no genuine detail band on this sheet

    // 2. Last data row = the greatest row that still holds a value (the merge trims
    //    everything below the band, so this is the band's bottom).
    let lastRow = bandTop;
    for (const m of xml.matchAll(/<c r="[A-Z]+(\d+)"[^>]*>\s*(?:<v>|<is>|<f)/g)) {
      const r = parseInt(m[1]!, 10);
      if (r > lastRow) lastRow = r;
    }
    if (lastRow <= bandTop) return xml;                // single-row band — nothing to replicate

    // 3. Replicate every band-top block for rows bandTop+1 … lastRow, row-shifting
    //    the sqref and the formulas, with fresh unique priorities.
    let nextPri = maxPri;
    const out = xml.replace(BLOCK, (block) => {
      const sq = /sqref="([^"]*)"/.exec(block)?.[1] ?? '';
      const anchor = Math.min(...sq.split(/\s+/).filter(Boolean).map(a => rowOfA1(a.split(':')[0]!)));
      if (anchor !== bandTop) return block;          // header/slope blocks stay single
      let copies = '';
      for (let row = bandTop + 1; row <= lastRow; row++) {
        const offset = row - bandTop;
        let copy = block.replace(/sqref="([^"]*)"/, (_m, s: string) => `sqref="${shiftSqrefRow(s, offset)}"`);
        // Shift references inside every <formula>… only (never attribute text).
        copy = copy.replace(/<formula>([\s\S]*?)<\/formula>/g, (_m, f: string) => `<formula>${shiftRefsRow(f, offset)}</formula>`);
        // Give each replicated rule a unique priority (Excel requires uniqueness).
        copy = copy.replace(/priority="\d+"/g, () => `priority="${++nextPri}"`);
        copies += copy;
      }
      return block + copies;
    });
    return out;
  }
}
